/**
 * usePanelSelection - Solar panel selection state management
 *
 * Manages panel catalog selection including:
 * - Available panels list
 * - Selected panel
 * - Brand filtering
 * - Loading state
 */

import { useState, useCallback } from 'react';

export interface PanelData {
  id: string;
  brand: string;
  model: string;
  power_watts: number;
  width_mm: number;
  height_mm: number;
  efficiency?: number;
  [key: string]: any;
}

export interface PanelSelectionState {
  availablePanels: PanelData[];
  selectedPanel: PanelData | null;
  panelsLoading: boolean;
  selectedBrand: string;
  availableBrands: string[];
}

export interface PanelSelectionActions {
  setAvailablePanels: React.Dispatch<React.SetStateAction<PanelData[]>>;
  setSelectedPanel: (panel: PanelData | null) => void;
  setPanelsLoading: (loading: boolean) => void;
  setSelectedBrand: (brand: string) => void;
  setAvailableBrands: React.Dispatch<React.SetStateAction<string[]>>;

  // Convenience methods
  selectPanelById: (id: string) => void;
  clearPanelSelection: () => void;
  getPanelsByBrand: (brand: string) => PanelData[];
}

export function usePanelSelection(): PanelSelectionState & PanelSelectionActions {
  const [availablePanels, setAvailablePanels] = useState<PanelData[]>([]);
  const [selectedPanel, setSelectedPanel] = useState<PanelData | null>(null);
  const [panelsLoading, setPanelsLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string>('TW Solar');
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);

  const selectPanelById = useCallback((id: string) => {
    const panel = availablePanels.find(p => p.id === id);
    if (panel) {
      setSelectedPanel(panel);
    }
  }, [availablePanels]);

  const clearPanelSelection = useCallback(() => {
    setSelectedPanel(null);
  }, []);

  const getPanelsByBrand = useCallback((brand: string) => {
    return availablePanels.filter(p => p.brand === brand);
  }, [availablePanels]);

  return {
    // State
    availablePanels,
    selectedPanel,
    panelsLoading,
    selectedBrand,
    availableBrands,

    // Setters
    setAvailablePanels,
    setSelectedPanel,
    setPanelsLoading,
    setSelectedBrand,
    setAvailableBrands,

    // Convenience methods
    selectPanelById,
    clearPanelSelection,
    getPanelsByBrand,
  };
}

export default usePanelSelection;
